import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import {
  boardSwitcherSearchSharedState,
  useBoardSwitcherMode,
} from '@trello/board-switcher';
import { Key, Scope, useShortcut } from '@trello/keybindings';
import { getScreenFromUrl } from '@trello/marketing-screens';
import { useIsBoardOrCardRoute } from '@trello/router';
import { useSharedStateSelector } from '@trello/shared-state';
import {
  useIsBoardPanelOpen,
  useIsInboxPanelOpen,
  useIsPlannerPanelOpen,
  useSplitScreenSharedState,
} from '@trello/split-screen';

import { useIsKeyboardShortcutsEnabled } from 'app/src/components/Shortcuts/useIsKeyboardShortcutsEnabled';
import { BoardSwitcher } from './BoardSwitcher';
import { useBoardSwitcherPrefetchLazyQuery } from './BoardSwitcherPrefetchQuery.generated';
import { useBoardSwitcherKeyboardNav } from './useBoardSwitcherKeyboardNav';

import * as styles from './BoardSwitcherPanel.module.less';

export const BoardSwitcherPanel: FunctionComponent = () => {
  const memberId = useMemberId();
  const panelRef = useRef<HTMLDivElement>(null);

  const isBoardOrCardRoute = useIsBoardOrCardRoute();

  const isKeyboardShortcutsEnabled = useIsKeyboardShortcutsEnabled();
  const isShortcutEnabled = isKeyboardShortcutsEnabled && isBoardOrCardRoute;

  const mode = useBoardSwitcherMode();

  const {
    panels,
    configuration,
    toggleInbox,
    togglePlanner,
    toggleBoard,
    toggleSwitcher,
  } = useSplitScreenSharedState();

  const searchFieldRef = useSharedStateSelector(
    boardSwitcherSearchSharedState,
    useCallback((state) => state.searchFieldRef, []),
  );

  const isInboxOpen = useIsInboxPanelOpen();
  const isPlannerOpen = useIsPlannerPanelOpen();
  const isBoardOpen = useIsBoardPanelOpen();

  const shortcutHandler = useCallback(() => {
    if (configuration === 'tabs') {
      if (panels.inbox) {
        toggleInbox();
      } else if (panels.planner) {
        togglePlanner();
      } else if (panels.board) {
        toggleBoard();
      }
    }

    if (!panels.switcher) {
      toggleSwitcher();
    }
    searchFieldRef?.current?.focus();

    Analytics.sendPressedShortcutEvent({
      shortcutName: 'boardSwitcherShortcut',
      keyValue: 'b',
      source: getScreenFromUrl(),
      attributes: {
        isInboxOpen,
        isPlannerOpen,
        isBoardOpen,
      },
    });
  }, [
    configuration,
    isBoardOpen,
    isInboxOpen,
    isPlannerOpen,
    panels.board,
    panels.inbox,
    panels.planner,
    panels.switcher,
    searchFieldRef,
    toggleBoard,
    toggleInbox,
    togglePlanner,
    toggleSwitcher,
  ]);

  useShortcut(shortcutHandler, {
    scope: Scope.Global,
    key: Key.b,
    enabled: isShortcutEnabled,
  });

  useBoardSwitcherKeyboardNav({
    containerElement: panelRef?.current,
    searchElement: searchFieldRef?.current,
  });

  const [prefetchBoardSwitcherData] = useBoardSwitcherPrefetchLazyQuery();

  useEffect(() => {
    prefetchBoardSwitcherData({
      variables: { memberId },
    });
    // We only want to fire this once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mode !== 'panel') {
    return null;
  }

  return (
    <div ref={panelRef} className={styles.boardSwitcherPanel}>
      <BoardSwitcher isPanel />
    </div>
  );
};
